import type { EChartsOption, EChartsSeriesData, TooltipFormatterParam } from '../types/chart.types';
import { formatTime } from '@/shared/utils/dateFormatter';
import { formatToRoundedDecimal } from '@/shared/utils/numberFormatter';
import { CHART_COLORS } from '@/shared/constants/colors';
import { DateFactory } from '@/shared/utils/dateFactory';

/**
 * ツールチップ桁数のフォールバック既定値。
 * 通常フローでは全系列がDATA_TYPE_CONFIG由来のprecisionを持つため到達しない防御的既定（整数表示）。
 */
const DEFAULT_TOOLTIP_DECIMAL_PLACES = 0;

/**
 * EChartsオプション生成ファクトリ
 */
export class ChartOptionsFactory {
  /**
   * リアルタイムチャート用のオプションを生成
   * @param params.seriesData シリーズデータ配列
   * @param params.originalDataPoints 元のデータポイント（ギャップ検出用）
   * @param params.isMobile モバイル表示かどうか
   * @returns EChartsオプション
   */
  static createRealtimeChartOptions(params: {
    seriesData: EChartsSeriesData[];
    originalDataPoints?: Array<{ timestamp: Date }>;
    isMobile?: boolean;
  }): EChartsOption {
    const { seriesData, originalDataPoints } = params;
    const isMobile = params.isMobile ?? false;
    const legendData = seriesData.map((series) => series.name);

    // 当日の0:00と23:59を計算
    const now = DateFactory.now();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    // 元のデータポイントからmarkAreaを生成（線切断用nullを除外）
    const nullRanges = originalDataPoints
      ? this.collectZeroRangesFromOriginalData(originalDataPoints)
      : this.collectZeroRanges(seriesData);

    // seriesDataをそのまま使用（nullは線切断のために必要）
    const filteredSeriesData = seriesData;

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: this.createTooltipFormatter(seriesData),
      },
      legend: {
        data: legendData,
        bottom: '10px',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: isMobile ? '120px' : '80px',
        top: '30px',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        min: startOfDay.getTime(),
        max: endOfDay.getTime(),
        axisLabel: {
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
        },
      ],
      series: [
        ...filteredSeriesData,
        // グレーマスクを表示するための空のseries
        ...(nullRanges.length > 0
          ? [
              {
                name: 'データなし期間',
                type: 'line' as const,
                data: [],
                smooth: false,
                symbol: 'none',
                symbolSize: 0,
                lineStyle: {
                  width: 0,
                },
                itemStyle: {
                  color: 'transparent',
                },
                markArea: {
                  silent: true,
                  itemStyle: {
                    color: CHART_COLORS.NO_DATA_MASK,
                  },
                  data: nullRanges,
                },
              },
            ]
          : []),
      ],
    };
  }

  /**
   * ツールチップのフォーマッター関数を生成
   * @param seriesData シリーズデータ配列（unit情報を含む）
   * @returns ECharts tooltip用のフォーマッター関数
   */
  private static createTooltipFormatter(
    seriesData: EChartsSeriesData[]
  ): (params: TooltipFormatterParam[]) => string {
    const unitBySeriesName = new Map<string, string>();
    const precisionBySeriesName = new Map<string, number>();
    seriesData.forEach((series) => {
      if (series.unit) {
        unitBySeriesName.set(series.name, series.unit);
      }
      if (series.precision !== undefined) {
        precisionBySeriesName.set(series.name, series.precision);
      }
    });

    return (params: TooltipFormatterParam[]): string => {
      if (params.length === 0) {
        return '';
      }

      const timeLabel = formatTime(DateFactory.fromTimestamp(Number(params[0].value[0])));

      const lines = params.map((param) => {
        const unit = unitBySeriesName.get(param.seriesName) ?? '';
        const decimalPlaces = precisionBySeriesName.get(param.seriesName) ?? DEFAULT_TOOLTIP_DECIMAL_PLACES;
        const rawValue = param.value[1];
        const formattedValue = rawValue !== null ? `${formatToRoundedDecimal({ value: rawValue, decimalPlaces })} ${unit}` : '-';
        return `${param.marker} ${param.seriesName}: ${formattedValue}`;
      });

      return `${timeLabel}<br/>${lines.join('<br/>')}`;
    };
  }

  /**
   * 元のデータポイントからデータギャップを検出してmarkAreaを生成
   * @param dataPoints 元のデータポイント配列
   * @returns markArea用の範囲配列
   */
  private static collectZeroRangesFromOriginalData(
    dataPoints: Array<{ timestamp: Date }>
  ): Array<[{ xAxis: Date }, { xAxis: Date }]> {
    if (dataPoints.length === 0) {
      // データが全くない場合は、当日全体をグレーアウト
      const now = DateFactory.now();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return [[{ xAxis: startOfDay }, { xAxis: endOfDay }]];
    }

    // タイムスタンプを収集してソート
    const timestamps = dataPoints
      .map(point => point.timestamp.getTime())
      .sort((a, b) => a - b);

    const now = DateFactory.now();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const ranges: Array<[{ xAxis: Date }, { xAxis: Date }]> = [];

    // 0:00からデータ開始までの範囲をグレーアウト
    const firstDataTimestamp = timestamps[0];
    if (firstDataTimestamp > startOfDay.getTime()) {
      ranges.push([
        { xAxis: startOfDay },
        { xAxis: DateFactory.fromTimestamp(firstDataTimestamp) }
      ]);
    }

    // データポイント間のギャップをチェック
    for (let i = 0; i < timestamps.length - 1; i++) {
      const currentTimestamp = timestamps[i];
      const nextTimestamp = timestamps[i + 1];
      const gapMinutes = (nextTimestamp - currentTimestamp) / (1000 * 60);

      // 2分以上のギャップがある場合、その間をグレーアウト
      if (gapMinutes > 2) {
        ranges.push([
          { xAxis: DateFactory.fromTimestamp(currentTimestamp) },
          { xAxis: DateFactory.fromTimestamp(nextTimestamp) }
        ]);
      }
    }

    // データ終了から23:59までの範囲をグレーアウト
    const lastDataTimestamp = timestamps[timestamps.length - 1];
    if (lastDataTimestamp < endOfDay.getTime()) {
      ranges.push([
        { xAxis: DateFactory.fromTimestamp(lastDataTimestamp) },
        { xAxis: endOfDay }
      ]);
    }

    return ranges;
  }

  /**
   * 全シリーズでnull/undefinedの値を持つタイムスタンプを収集し、連続する範囲を特定
   * データが存在しない時間帯も含めて、0:00から23:59までの全範囲をチェック
   * @param seriesData シリーズデータ配列
   * @returns markArea用の範囲配列
   */
  private static collectZeroRanges(
    seriesData: EChartsSeriesData[]
  ): Array<[{ xAxis: Date }, { xAxis: Date }]> {
    if (seriesData.length === 0) {
      // データが全くない場合は、当日全体をグレーアウト
      const now = DateFactory.now();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return [[{ xAxis: startOfDay }, { xAxis: endOfDay }]];
    }

    // 全タイムスタンプを収集（nullデータは除外）
    const allTimestamps = new Set<number>();
    seriesData.forEach((series) => {
      series.data.forEach(([timestamp, value]) => {
        // nullでないデータポイントのタイムスタンプのみ収集
        if (value !== null) {
          allTimestamps.add(timestamp.getTime());
        }
      });
    });

    if (allTimestamps.size === 0) {
      // データポイントが全くない場合は、当日全体をグレーアウト
      const now = DateFactory.now();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return [[{ xAxis: startOfDay }, { xAxis: endOfDay }]];
    }

    const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);
    const now = DateFactory.now();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const ranges: Array<[{ xAxis: Date }, { xAxis: Date }]> = [];

    // 0:00からデータ開始までの範囲をグレーアウト
    const firstDataTimestamp = sortedTimestamps[0];
    if (firstDataTimestamp > startOfDay.getTime()) {
      ranges.push([
        { xAxis: startOfDay },
        { xAxis: DateFactory.fromTimestamp(firstDataTimestamp) }
      ]);
    }

    // データポイント間のギャップをチェック
    for (let i = 0; i < sortedTimestamps.length - 1; i++) {
      const currentTimestamp = sortedTimestamps[i];
      const nextTimestamp = sortedTimestamps[i + 1];
      const gapMinutes = (nextTimestamp - currentTimestamp) / (1000 * 60);

      // 2分以上のギャップがある場合、その間をグレーアウト
      if (gapMinutes > 2) {
        ranges.push([
          { xAxis: DateFactory.fromTimestamp(currentTimestamp) },
          { xAxis: DateFactory.fromTimestamp(nextTimestamp) }
        ]);
      }
    }

    // データ終了から23:59までの範囲をグレーアウト
    const lastDataTimestamp = sortedTimestamps[sortedTimestamps.length - 1];
    if (lastDataTimestamp < endOfDay.getTime()) {
      ranges.push([
        { xAxis: DateFactory.fromTimestamp(lastDataTimestamp) },
        { xAxis: endOfDay }
      ]);
    }

    return ranges;
  }


  /**
   * 過去データチャート用のオプションを生成
   * @param params.seriesData シリーズデータ配列
   * @param params.isMobile モバイル表示かどうか
   * @returns EChartsオプション
   */
  static createHistoryChartOptions(params: {
    seriesData: EChartsSeriesData[];
    isMobile?: boolean;
  }): EChartsOption {
    const { seriesData } = params;
    const isMobile = params.isMobile ?? false;
    const legendData = seriesData.map((series) => series.name);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: this.createTooltipFormatter(seriesData),
      },
      legend: {
        data: legendData,
        top: '10px',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: isMobile ? '140px' : '80px',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: 100,
        },
        {
          type: 'inside',
          xAxisIndex: 0,
        },
      ],
      series: seriesData,
    };
  }
}
